import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Lock } from "lucide-react";
import RenewalsList from "./RenewalsList";
import { LIKELINESS_OPTIONS, LIKELINESS_OPTIONS_UNPAID } from "./constants";

const SubscriptionModal = ({ 
  isOpen, 
  onClose, 
  formData, 
  setFormData, 
  selectedSubscription, 
  onSave,
  onAutoSave
}) => {
  const [unpaidConfirmIndex, setUnpaidConfirmIndex] = useState(null);
  const [initialUnpaidConfirm, setInitialUnpaidConfirm] = useState(false);
  const [priceInputFocused, setPriceInputFocused] = useState(false);

  // Format number as currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "";
    const num = parseFloat(value);
    if (isNaN(num)) return "";
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const shouldAutoGenerateRenewal = (likeliness) => {
    return !["CANCELLED", "FREE"].includes(likeliness);
  };

  // True if a renewal looks like the modal's own auto-generated stub —
  // unpaid AND notes left empty. Used to decide whether we can keep
  // refreshing it as the user edits the inputs above. Once the user marks
  // it paid OR types a note, we treat it as user-authored and stop touching
  // it (matches the "Locked" intuition for the initial sign-up block).
  const isAutoGenRenewal = (r) =>
    !!r && r.paid !== true && (r.notes == null || r.notes === "");

  // Single source of truth for the auto-generated first-renewal stub.
  // Builds the renewal array given the current inputs:
  //   - if there's no renewal yet → create one
  //   - if there's exactly one auto-gen-shaped unpaid renewal → refresh it
  //     in place so subsequent keystrokes (price, date) keep it in sync,
  //     and so toggling Payment Received refreshes likeliness from PENDING
  //     → YES on the renewal too
  //   - otherwise (user has typed in the renewal, or there are multiple)
  //     → leave renewals untouched
  // Was previously a guard that only ever created when length===0; that
  // bug is what stranded a "$2" renewal on screen when the user typed
  // "299" because the first keystroke created the stub and subsequent
  // keystrokes hit the !== 0 branch and skipped the update.
  const computeAutoGenRenewals = (currentRenewals, { signupDate, initialPrice, likeliness }) => {
    if (!signupDate || !initialPrice || !shouldAutoGenerateRenewal(likeliness)) {
      return currentRenewals;
    }
    const renewalDate = new Date(signupDate);
    renewalDate.setFullYear(renewalDate.getFullYear() + 1);
    const stub = {
      renewal_date: renewalDate.toISOString().split('T')[0],
      renewal_price: initialPrice,
      paid: false,
      likeliness: likeliness || "YES",
      notes: ""
    };
    if (currentRenewals.length === 0) {
      return [stub];
    }
    if (currentRenewals.length === 1 && isAutoGenRenewal(currentRenewals[0])) {
      return [stub];
    }
    return currentRenewals;
  };

  const handleSignupDateChange = (newDate) => {
    const updated = { ...formData, signup_date: newDate };
    updated.renewals = computeAutoGenRenewals(formData.renewals, {
      signupDate: newDate,
      initialPrice: formData.initial_price,
      likeliness: formData.likeliness,
    });
    setFormData(updated);
  };

  const handleInitialPriceChange = (newPrice) => {
    const updated = { ...formData, initial_price: newPrice };
    updated.renewals = computeAutoGenRenewals(formData.renewals, {
      signupDate: formData.signup_date,
      initialPrice: newPrice,
      likeliness: formData.likeliness,
    });
    setFormData(updated);
  };

  const handleRenewalChange = (index, field, value) => {
    const newRenewals = [...formData.renewals];
    newRenewals[index] = { ...newRenewals[index], [field]: value };
    
    if (field === 'paid' && value === true) {
      const currentRenewal = newRenewals[index];
      const currentLikeliness = currentRenewal.likeliness || formData.likeliness || "YES";
      if (index === newRenewals.length - 1 && currentRenewal.renewal_date && shouldAutoGenerateRenewal(currentLikeliness)) {
        const nextDate = new Date(currentRenewal.renewal_date);
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        const nextPrice = currentRenewal.renewal_price || formData.initial_price || "";
        newRenewals.push({
          renewal_date: nextDate.toISOString().split('T')[0],
          renewal_price: nextPrice,
          paid: false,
          likeliness: currentLikeliness,
          notes: ""
        });
      }
    }
    
    const updatedFormData = { ...formData, renewals: newRenewals };
    setFormData(updatedFormData);
    
    // Auto-save when paid status changes for existing subscriptions
    if (field === 'paid' && selectedSubscription && onAutoSave) {
      onAutoSave(updatedFormData);
    }
  };

  const handleUnpaidRequest = (index) => {
    setUnpaidConfirmIndex(index);
  };

  const handleConfirmUnpaid = () => {
    if (unpaidConfirmIndex !== null) {
      const newRenewals = [...formData.renewals];
      // Unmark as paid
      newRenewals[unpaidConfirmIndex] = { ...newRenewals[unpaidConfirmIndex], paid: false };
      
      // Check if the next renewal was auto-generated (i.e., exists and is unpaid)
      // Remove it if this was the last paid renewal that created it
      const nextIndex = unpaidConfirmIndex + 1;
      if (nextIndex < newRenewals.length && !newRenewals[nextIndex].paid) {
        // Remove the auto-created next renewal
        newRenewals.splice(nextIndex, 1);
      }
      
      const updatedFormData = { ...formData, renewals: newRenewals };
      setFormData(updatedFormData);
      setUnpaidConfirmIndex(null);
      
      // Auto-save after unmarking as paid
      if (selectedSubscription && onAutoSave) {
        onAutoSave(updatedFormData);
      }
    }
  };

  const handleCancelUnpaid = () => {
    setUnpaidConfirmIndex(null);
  };

  // Handle initial paid checkbox change
  const handleInitialPaidChange = (checked) => {
    if (formData.initial_paid && !checked) {
      // User is trying to unmark as paid - show confirmation
      setInitialUnpaidConfirm(true);
    } else {
      // Marking as paid — auto-promote PENDING → YES so the renewal-prediction
      // dropdown opens up. Mirrors the backend's auto-promote on PUT.
      const promotedLikeliness = checked && formData.likeliness === "PENDING" ? "YES" : formData.likeliness;
      const updated = { ...formData, initial_paid: checked, likeliness: promotedLikeliness };

      // Refresh the auto-gen renewal (or create one if missing). Important to
      // run this AFTER the likeliness promotion: a renewal stub that was
      // created mid-typing while likeliness was still PENDING needs its
      // likeliness updated to YES, otherwise the user sees a "PENDING" tag
      // on a renewal that should clearly be YES.
      updated.renewals = computeAutoGenRenewals(formData.renewals, {
        signupDate: formData.signup_date,
        initialPrice: formData.initial_price,
        likeliness: promotedLikeliness,
      });

      setFormData(updated);

      // Auto-save when paid status changes for existing subscriptions
      if (selectedSubscription && onAutoSave) {
        onAutoSave(updated);
      }
    }
  };

  const handleConfirmInitialUnpaid = () => {
    const updated = { ...formData, initial_paid: false };
    
    // Remove auto-generated first renewal if it exists and is unpaid
    if (formData.renewals.length > 0 && !formData.renewals[0].paid) {
      updated.renewals = [];
    }
    
    setFormData(updated);
    setInitialUnpaidConfirm(false);
    
    // Auto-save after unmarking as paid
    if (selectedSubscription && onAutoSave) {
      onAutoSave(updated);
    }
  };

  const handleCancelInitialUnpaid = () => {
    setInitialUnpaidConfirm(false);
  };

  const modalTitle = selectedSubscription ? "Edit Subscription" : "Add Subscription";
  const saveLabel = selectedSubscription ? "Update" : "Create";

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="subscription-modal">
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input 
                  value={formData.business_name} 
                  onChange={e => setFormData({ ...formData, business_name: e.target.value })} 
                  placeholder="Enter business name"
                  data-testid="subscription-business-name"
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-500 uppercase">Initial Sign Up</h3>
                {formData.initial_paid && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <span className="text-emerald-600 font-medium">Paid</span>
                    <Lock className="w-3 h-3 text-slate-400 ml-1" />
                    <span className="text-slate-400 text-xs">Locked</span>
                  </div>
                )}
              </div>
              <div className={`p-4 rounded-lg border ${formData.initial_paid ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className={formData.initial_paid ? 'text-emerald-700' : ''}>Sign Up Date</Label>
                    <Input
                      type="date"
                      value={formData.signup_date}
                      onChange={e => handleSignupDateChange(e.target.value)}
                      disabled={formData.initial_paid}
                      className={formData.initial_paid ? 'bg-emerald-100/50 text-emerald-800 border-emerald-300 cursor-not-allowed opacity-75' : ''}
                      data-testid="subscription-signup-date"
                    />
                  </div>
                  <div>
                    <Label className={formData.initial_paid ? 'text-emerald-700' : ''}>Initial Price</Label>
                    <div className="relative">
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${formData.initial_paid ? 'text-emerald-600' : 'text-slate-500'}`}>$</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={priceInputFocused ? formData.initial_price : formatCurrency(formData.initial_price)}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          handleInitialPriceChange(val);
                        }}
                        onFocus={() => setPriceInputFocused(true)}
                        onBlur={() => setPriceInputFocused(false)}
                        disabled={formData.initial_paid}
                        className={`pl-7 ${formData.initial_paid ? 'bg-emerald-100/50 text-emerald-800 border-emerald-300 cursor-not-allowed opacity-75' : ''}`}
                        placeholder="0.00"
                        data-testid="subscription-initial-price"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className={formData.initial_paid ? 'text-emerald-700' : ''}>Status</Label>
                    <Select
                      value={formData.likeliness}
                      onValueChange={val => setFormData({ ...formData, likeliness: val })}
                    >
                      <SelectTrigger
                        className={formData.initial_paid ? 'bg-emerald-100/50 text-emerald-800 border-emerald-300' : ''}
                        data-testid="subscription-initial-likeliness"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(formData.initial_paid ? LIKELINESS_OPTIONS : LIKELINESS_OPTIONS_UNPAID).map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.initial_paid}
                        onCheckedChange={handleInitialPaidChange}
                        data-testid="subscription-initial-paid"
                      />
                      <Label>Payment Received</Label>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className={formData.initial_paid ? 'text-emerald-700' : ''}>Initial Notes</Label>
                  <Textarea 
                    value={formData.initial_notes || ''} 
                    onChange={e => setFormData({ ...formData, initial_notes: e.target.value })}
                    disabled={formData.initial_paid}
                    placeholder="Add notes about the initial sign up..."
                    rows={2}
                    className={formData.initial_paid ? 'bg-emerald-100/50 text-emerald-800 border-emerald-300 cursor-not-allowed opacity-75' : ''}
                    data-testid="subscription-initial-notes"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400">When you enter both date and price, a renewal will auto-generate 12 months ahead</p>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase">Renewals</h3>
              <RenewalsList 
                renewals={formData.renewals} 
                onRenewalChange={handleRenewalChange}
                onUnpaidRequest={handleUnpaidRequest}
              />
            </div>
            
            <div>
              <Label>General Notes</Label>
              <Textarea 
                value={formData.notes} 
                onChange={e => setFormData({ ...formData, notes: e.target.value })} 
                placeholder="Add general notes about this subscription..." 
                rows={2}
                data-testid="subscription-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={onSave} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="subscription-save-btn"
            >
              {saveLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Unmarking Paid */}
      <AlertDialog open={unpaidConfirmIndex !== null} onOpenChange={(open) => !open && handleCancelUnpaid()}>
        <AlertDialogContent data-testid="unpaid-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Unmarking this renewal as paid will also remove the auto-generated next renewal period. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelUnpaid} data-testid="unpaid-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmUnpaid}
              className="bg-red-600 hover:bg-red-700"
              data-testid="unpaid-confirm-yes"
            >
              Yes, unmark as paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog for Unmarking Initial Paid */}
      <AlertDialog open={initialUnpaidConfirm} onOpenChange={(open) => !open && handleCancelInitialUnpaid()}>
        <AlertDialogContent data-testid="initial-unpaid-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Unmarking the initial payment as received will unlock the sign up date and initial price fields for editing. If a renewal was auto-generated, it will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelInitialUnpaid}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmInitialUnpaid}
              className="bg-red-600 hover:bg-red-700"
              data-testid="initial-unpaid-confirm-yes"
            >
              Yes, unmark as paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SubscriptionModal;
